"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useStudio } from "@/contexts/studio-context";

/**
 * Le seul chemin qui mène un morceau de la bibliothèque jusqu'au piano.
 *
 * # Pourquoi c'est isolé ici
 *
 * Ouvrir un morceau charge la pièce puis navigue vers `/studio`, où le Studio
 * fait apparaître de lui-même `PieceOverviewOverlay` — l'écran de présentation
 * du morceau, avec ses boutons *Apprendre* et *ou explorer librement*.
 *
 * Il y a donc deux écrans avant de jouer : la fiche du catalogue, puis
 * l'aperçu. On a essayé de court-circuiter l'aperçu depuis la fiche — c'était
 * une erreur, et elle est instructive : **l'aperçu n'est pas un doublon de la
 * fiche, c'est l'écran qui donne envie de jouer.** Il montre la tessiture, la
 * cadence de référence et la difficulté mesure par mesure, sur le morceau
 * réellement chargé, ce que la fiche ne peut pas faire avec les seules
 * métadonnées de catalogue.
 *
 * Si la redondance doit disparaître un jour, c'est donc la **fiche** qui
 * s'allège — titre, silhouette, un bouton — et non l'aperçu qui saute.
 *
 * # Ce que le hook expose
 *
 * `launching` et `error` sont rendus à l'appelant plutôt que peints ici : la
 * fiche affiche l'échec sous ses boutons, « Mes morceaux » sur la ligne
 * concernée. Un composant qui déciderait des deux imposerait sa mise en page
 * aux deux.
 *

 */
export function useOpenPiece(): {
  /** `scoreId` pour rouvrir un fichier précis de l'œuvre. */
  openPiece: (pieceId: string, scoreId?: string) => Promise<void>;
  launching: boolean;
  error: string | null;
} {
  const router = useRouter();
  const { loadPiece } = useStudio();
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPiece = useCallback(
    async (pieceId: string, scoreId?: string) => {
      setLaunching(true);
      setError(null);

      try {
        await loadPiece(pieceId, scoreId);
        router.push("/studio");
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Échec du chargement du morceau",
        );
      } finally {
        setLaunching(false);
      }
    },
    [loadPiece, router],
  );

  return { openPiece, launching, error };
}
